import React, { useState, useEffect } from 'react';
import './Podcasts.css';
import Footer from '../../components/Footer/Footer';
import { fetchPodcasts } from '../../services/api';

const Podcasts = () => {
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'KU';
  });
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedLang = localStorage.getItem('selectedLanguage') || 'KU';
    setCurrentLang(savedLang);

    const handleLanguageChange = (event) => {
      setCurrentLang(event.detail);
      localStorage.setItem('selectedLanguage', event.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  useEffect(() => {
    const getPodcasts = async () => {
      setLoading(true);
      try {
        const data = await fetchPodcasts();
        // Transform API data to match component's expected format
        const transformedPodcasts = data.map(podcast => ({
          id: podcast.id,
          image: podcast.image || '',
          date: podcast.date || '',
          EN: {
            title: podcast.title?.EN || '',
            description: podcast.description?.EN || '',
            link: podcast.link || '',
            linkText: podcast.linkText?.EN || 'Listen Now'
          },
          KU: {
            title: podcast.title?.KU || '',
            description: podcast.description?.KU || '',
            link: podcast.link || '',
            linkText: podcast.linkText?.KU || 'گوێگرتن'
          },
          AR: {
            title: podcast.title?.AR || '',
            description: podcast.description?.AR || '',
            link: podcast.link || '',
            linkText: podcast.linkText?.AR || 'استمع الآن'
          },
          TR: {
            title: podcast.title?.TR || '',
            description: podcast.description?.TR || '',
            link: podcast.link || '',
            linkText: podcast.linkText?.TR || 'Şimdi Dinle'
          }
        }));
        setPodcasts(transformedPodcasts);
      } catch (error) {
        console.error("Failed to fetch podcasts:", error);
        setPodcasts([]); // Fallback to empty array on error
      } finally {
        setLoading(false);
      }
    };
    getPodcasts();
  }, []);

  const content = {
    EN: {
      title: 'Our Podcasts',
      subtitle: 'Discover our collection of insightful podcasts covering Islamic teachings, history, culture, and spiritual guidance.',
      watchNow: 'Watch Now',
      listenNow: 'Listen Now'
    },
    KU: {
      title: 'پۆدکاستەکانمان',
      subtitle: 'کۆمەڵە پۆدکاستە بەنرخەکانمان بزانە کە فێرکردن، مێژوو، کلتور و ڕێنمایەتی ئاینییەکانی ئیسلام دەگرێتەوە.',
      watchNow: 'بینین',
      listenNow: 'گوێگرتن'
    },
    AR: {
      title: 'بودكاستاتنا',
      subtitle: 'اكتشف مجموعتنا من البودكاست الثاقبة التي تغطي تعاليم الإسلام والتاريخ والثقافة والإرشاد الروحي.',
      watchNow: 'شاهد الآن',
      listenNow: 'استمع الآن'
    },
    TR: {
      title: 'Podcastlerimiz',
      subtitle: 'İslami öğretiler, tarih, kültür ve manevi rehberliği kapsayan içgörülü podcast koleksiyonumuzu keşfedin.',
      watchNow: 'Şimdi İzle',
      listenNow: 'Şimdi Dinle'
    }
  };

  const pageContent = content[currentLang] || content.KU;

  return (
    <div className="podcasts-page">
      <div className="container">
        <div className="podcasts-content">
          {/* Title Section */}
          <div className="podcasts-title-section">
            <h1 className="page-title">{pageContent.title}</h1>
            <p className="page-subtitle">{pageContent.subtitle}</p>
          </div>

          {/* Podcasts Grid */}
          {loading ? (
            <div className="loading-message">Loading podcasts...</div>
          ) : podcasts.length === 0 ? (
            <div className="no-content-message">No podcasts found.</div>
          ) : (
            <div className="podcasts-grid">
              {podcasts.map((podcast) => {
                const podcastContent = podcast[currentLang] || podcast.EN;
                return (
                  <div key={podcast.id} className="podcast-card">
                    {/* Podcast Image */}
                    <div className="podcast-image-container">
                      {podcast.image ? (
                        <img
                          src={podcast.image}
                          alt={podcastContent.title || 'Podcast'}
                          className="podcast-image"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22225%22%3E%3Crect fill=%22%23e5e7eb%22 width=%22400%22 height=%22225%22/%3E%3Ctext fill=%22%239ca3af%22 font-family=%22sans-serif%22 font-size=%2214%22 dy=%2210.5%22 font-weight=%22bold%22 x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      ) : (
                        <div className="podcast-image-placeholder">
                          <div className="play-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      )}
                      <div className="podcast-image-overlay">
                        <div className="play-icon">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Podcast Content */}
                    <div className="podcast-content">
                      <h2 className="podcast-title">{podcastContent.title || 'Untitled Podcast'}</h2>
                      {podcast.date && (
                        <p className="podcast-date">
                          <svg className="date-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          {podcast.date}
                        </p>
                      )}
                      <p className="podcast-description">{podcastContent.description || 'No description available.'}</p>

                      {/* Link Button */}
                      {podcastContent.link && (
                        <a
                          href={podcastContent.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="podcast-link-button"
                        >
                          <span>{podcastContent.linkText || 'Listen Now'}</span>
                          <svg className="link-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Podcasts;

